import type { ChangeEvent, ReactNode } from "react";

/** One control system for the intake: 48px minimum height, hairline border, ink focus ring. */
export const CONTROL_CLASS =
  "mt-2 block min-h-12 w-full rounded-lg border border-hairline bg-paper-raised px-4 text-base text-ink " +
  "placeholder:text-slate/70 transition-[border-color,box-shadow] duration-150 " +
  "focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/15 " +
  "aria-[invalid=true]:border-signal-ink disabled:opacity-60";

export const LABEL_CLASS = "block text-sm font-medium text-slate-strong";
export const HINT_CLASS = "mt-2 text-xs leading-5 text-slate";
export const ERROR_CLASS = "mt-2 text-xs font-medium leading-5 text-signal-ink";

export function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return <p id={id} role="alert" className={ERROR_CLASS}>{message}</p>;
}

export function RequiredMark() {
  return <span aria-hidden="true" className="text-signal-ink"> *</span>;
}

type TextFieldProps = {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  required?: boolean;
  type?: "text" | "email" | "number" | "tel";
  inputMode?: "numeric" | "email" | "tel" | "text";
  maxLength?: number;
  max?: number;
  autoComplete?: string;
  placeholder?: string;
  suggestions?: string[];
};

export function TextField({ id, name, label, value, onChange, error, hint, required, type = "text", inputMode, maxLength, max, autoComplete, placeholder, suggestions }: TextFieldProps) {
  const listId = suggestions ? `${id}-options` : undefined;
  const describedBy = [error ? `${id}-error` : null, hint && !error ? `${id}-hint` : null].filter(Boolean).join(" ") || undefined;
  return (
    <div>
      <label htmlFor={id} className={LABEL_CLASS}>{label}{required ? <RequiredMark /> : null}</label>
      <input
        id={id}
        name={name}
        className={CONTROL_CLASS}
        type={type}
        inputMode={inputMode}
        required={required}
        min={type === "number" ? 1 : undefined}
        step={type === "number" ? 1 : undefined}
        max={max}
        maxLength={maxLength}
        autoComplete={autoComplete}
        placeholder={placeholder}
        list={listId}
        value={value}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
      />
      {suggestions && listId ? <datalist id={listId}>{suggestions.map((option) => <option key={option} value={option} />)}</datalist> : null}
      {hint && !error ? <p id={`${id}-hint`} className={HINT_CLASS}>{hint}</p> : null}
      <FieldError id={`${id}-error`} message={error} />
    </div>
  );
}

type SelectFieldProps = {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly { value: string; label: string }[];
  error?: string;
  hint?: string;
};

export function SelectField({ id, name, label, value, onChange, options, error, hint }: SelectFieldProps) {
  const describedBy = [error ? `${id}-error` : null, hint && !error ? `${id}-hint` : null].filter(Boolean).join(" ") || undefined;
  return (
    <div>
      <label htmlFor={id} className={LABEL_CLASS}>{label}</label>
      <select id={id} name={name} className={`${CONTROL_CLASS} pr-10`} value={value} onChange={(event) => onChange(event.target.value)} aria-invalid={error ? true : undefined} aria-describedby={describedBy}>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
      {hint && !error ? <p id={`${id}-hint`} className={HINT_CLASS}>{hint}</p> : null}
      <FieldError id={`${id}-error`} message={error} />
    </div>
  );
}

type SectionPanelProps = {
  id: string;
  step: number;
  title: string;
  description: string;
  children: ReactNode;
  hidden?: boolean;
  actions?: ReactNode;
};

/** A white stage panel: numbered heading, one-line purpose, then the fields. */
export function SectionPanel({ id, step, title, description, children, hidden, actions }: SectionPanelProps) {
  return (
    <section id={id} hidden={hidden} aria-labelledby={`${id}-title`} className="scroll-mt-36 rounded-[10px] border border-hairline bg-paper-raised p-6 sm:p-8">
      <header className="flex flex-wrap items-start justify-between gap-4 border-b border-hairline pb-5">
        <div className="flex items-start gap-4">
          <span aria-hidden="true" className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-accent-soft font-display text-sm font-semibold text-ink">{String(step).padStart(2, "0")}</span>
          <div>
            <h2 id={`${id}-title`} className="font-display text-xl font-semibold tracking-tight text-ink">{title}</h2>
            <p className="mt-1 text-sm leading-6 text-slate">{description}</p>
          </div>
        </div>
        {actions}
      </header>
      <div className="pt-6">{children}</div>
    </section>
  );
}
